import { useMutation } from '@tanstack/react-query';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { Button } from '@doger/ui/components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@doger/ui/components/ui/input-otp.component';
import { $$toast } from '@doger/ui/components/ui/toast';
import { useAppForm } from '@doger/ui/form';
import { $$api, ErrorMatcher } from '@doger/core/shared/api';

import styles from './confirm-email-form.module.scss';

type ConfirmEmailFormProps = {
  className?: string;
  email: string;
  onSuccess: (tokens: { accessToken: string; refreshToken: string }) => void;
};

export const ConfirmEmailForm = ({
  className,
  email,
  onSuccess,
}: ConfirmEmailFormProps) => {
  const inputKeyRef = useRef(0);
  const { t } = useTranslation('core');

  const confirmEmailSchema = z.object({
    code: z.string().length(6, t('validation.auth.confirmationCodeRequired')),
  });

  const form = useAppForm({
    defaultValues: {
      code: '',
    },
    validators: {
      onSubmit: confirmEmailSchema,
    },
    onSubmit: async ({ value }) => {
      await confirmEmail
        .mutateAsync({
          code: value.code,
        })
        .catch(() => {
          /**
           * @see https://github.com/guilhermerodz/input-otp/issues/83#issuecomment-2829385413
           */
          inputKeyRef.current++;
        });
    },
  });

  const confirmEmail = useMutation({
    mutationKey: ['auth', 'confirm-email'],
    mutationFn: async (data: z.infer<typeof confirmEmailSchema>) => {
      await $$api.confirmEmail({
        code: data.code.toString(),
      });

      const { accessToken, refreshToken } =
        await $$api.tokensService.refreshTokens();

      return { accessToken, refreshToken };
    },

    onSuccess: ({ accessToken, refreshToken }) => {
      $$toast.success(t('auth.toasts.emailConfirmed'));
      onSuccess({ accessToken, refreshToken });
    },
    onError: (error) => {
      if (
        ErrorMatcher.isAppError(error, ['INVALID_CONFIRMATION_EMAIL_TOKEN'])
      ) {
        $$toast.error(t('auth.toasts.invalidConfirmationCode'));

        return;
      }

      if (ErrorMatcher.isAppError(error, ['EMAIL_ALREADY_CONFIRMED'])) {
        $$toast.info(t('auth.toasts.emailAlreadyConfirmed'));
        onSuccess(
          $$api.tokensService.getTokens() as {
            accessToken: string;
            refreshToken: string;
          }
        );

        return;
      }

      $$toast.error(t('auth.toasts.failedToConfirmEmail'));
    },
  });

  const resendEmailConfirmation = useMutation({
    mutationKey: ['auth', 'resend-email-confirmation'],
    mutationFn: async () => {
      const response = await $$api.resendEmailConfirmation();

      return response;
    },
    onSuccess: () => {
      $$toast.success(t('auth.confirmEmail.resendSuccess'));
    },
    onError: (error) => {
      if (
        ErrorMatcher.isAppError(error, ['TOO_MANY_RESEND_EMAIL_CONFIRMATION'])
      ) {
        const remainingTimeSeconds = error.response!.data?.details
          ?.remainingTime as number | undefined;

        if (!remainingTimeSeconds) {
          $$toast.error(error.response!.data.message);

          return;
        }

        $$toast.error(
          t('auth.confirmEmail.resendErrorWithRemainingTime', {
            replace: {
              remainingTime: remainingTimeSeconds,
            },
          })
        );

        return;
      }

      $$toast.error(t('auth.confirmEmail.resendError'));
    },
  });

  return (
    <div className={className}>
      <div className={styles.header}>
        <p className={styles.description}>
          {t('auth.confirmEmail.description')} <strong>{email}</strong>
        </p>
        <p className={styles.instruction}>
          {t('auth.confirmEmail.instruction')}
        </p>
      </div>

      <form
        name='confirm-email-form'
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <div className={styles.fields}>
          <form.Field
            name='code'
            children={(field) => (
              <form.Subscribe selector={(state) => [state.isSubmitting]}>
                {([isSubmitting]) => (
                  <InputOTP
                    key={inputKeyRef.current}
                    className={styles.inputOTP}
                    maxLength={6}
                    value={field.state.value}
                    onChange={(value) => {
                      field.handleChange(value);
                    }}
                    onComplete={() => {
                      field.form.handleSubmit();
                    }}
                    disabled={isSubmitting}
                    autoFocus
                    pattern={REGEXP_ONLY_DIGITS}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                )}
              </form.Subscribe>
            )}
          />
        </div>

        <form.AppForm>
          <form.SubmitButton
            variant='contained'
            size='lg'
            palette='primary'
            children={({ isSubmitting }) =>
              isSubmitting
                ? t('auth.confirmEmail.submitting')
                : t('auth.confirmEmail.submitButton')
            }
          />
        </form.AppForm>
      </form>

      <div className={styles.resendSection}>
        {t('auth.confirmEmail.resendText')}
        <Button
          className={styles.resendButton}
          type='button'
          variant='text'
          size='xs'
          palette='primary'
          onClick={() => resendEmailConfirmation.mutateAsync()}
          disabled={resendEmailConfirmation.isPending}
        >
          {resendEmailConfirmation.isPending
            ? t('auth.confirmEmail.resendSubmitting')
            : t('auth.confirmEmail.resendButton')}
        </Button>
      </div>
    </div>
  );
};
